import React, { useEffect, useRef, useState } from 'react';
import { ShieldCheck, ShieldAlert, Upload, Loader2, Clock, Camera, X } from 'lucide-react';
import { apiClient } from '../../api/client';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ParentVerificationCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const load = async () => {
    try { setStatus(await apiClient.request('/api/verification/status')); } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => () => { streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);

  if (!status || !status.required || status.status === 'verified') return null;

  const submit = async (file) => {
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/verification/submit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token') || ''}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setStatus({ ...status, status: data.status, reason: data.reason, required: data.status !== 'verified' });
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOn(false);
  };

  const startCamera = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 } } });
      streamRef.current = stream;
      setCameraOn(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 60);
    } catch {
      setError('Camera unavailable — use "Upload a photo" instead.');
    }
  };

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement('canvas');
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    c.toBlob((b) => {
      if (b) { stopCamera(); submit(new File([b], 'verification.jpg', { type: 'image/jpeg' })); }
    }, 'image/jpeg', 0.92);
  };

  const pending = status.status === 'pending' || status.status === 'manual_review';

  return (
    <div className="rounded-xl border p-4 mb-4" data-testid="parent-verification-card"
      style={{ backgroundColor: pending ? '#FFF7ED' : '#FEF2F2', borderColor: pending ? '#FED7AA' : '#FECACA' }}>
      <div className="flex items-start gap-3">
        {pending ? <Clock size={20} className="mt-0.5 text-amber-600" /> : <ShieldAlert size={20} className="mt-0.5 text-red-600" />}
        <div className="flex-1">
          {pending ? (
            <>
              <p className="font-semibold text-sm text-amber-800">Identity verification under review</p>
              <p className="text-xs text-amber-700 mt-1">We're reviewing your photo — you'll get full parent access once approved (usually within a day).</p>
            </>
          ) : (
            <>
              <p className="font-semibold text-sm text-red-800">Verify your identity to unlock parent features</p>
              <p className="text-xs text-red-700 mt-1">
                To keep every teen on HiBFF safe, parents verify who they are: one photo of yourself holding your passport or driving licence next to your face. Checked instantly.
                {status.reason && status.status === 'rejected' ? ` Previous attempt: ${status.reason}` : ''}
              </p>

              {cameraOn ? (
                <div className="mt-3" data-testid="verification-camera">
                  <div className="relative rounded-xl overflow-hidden bg-black max-w-md">
                    <video ref={videoRef} playsInline muted className="w-full" style={{ transform: 'scaleX(-1)' }} />
                    {/* Framing guides */}
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute border-2 border-emerald-400 rounded-full"
                        style={{ left: '12%', top: '14%', width: '34%', height: '62%' }}>
                        <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-emerald-300 whitespace-nowrap">Align your face here</span>
                      </div>
                      <div className="absolute border-2 border-sky-400 rounded-md"
                        style={{ right: '10%', top: '28%', width: '34%', height: '34%' }}>
                        <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-sky-300 whitespace-nowrap">Hold your ID here</span>
                      </div>
                      <p className="absolute top-2 left-1/2 -translate-x-1/2 text-[10px] text-white bg-black/60 px-2 py-1 rounded-full whitespace-nowrap">Good light · no glare on the ID · whole document visible</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button onClick={capture} disabled={busy}
                      className="px-4 py-2 rounded-lg text-white text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
                      style={{ backgroundColor: '#059669' }} data-testid="verification-capture-btn">
                      {busy ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />} Take photo
                    </button>
                    <button onClick={stopCamera}
                      className="px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1 border border-gray-300 text-gray-600 bg-white"
                      data-testid="verification-camera-cancel">
                      <X size={13} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={startCamera} disabled={busy}
                    className="px-4 py-2 rounded-lg text-white text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: '#DC2626' }} data-testid="verification-camera-btn">
                    <Camera size={13} /> Use my camera (guided)
                  </button>
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                    onChange={(e) => e.target.files?.[0] && submit(e.target.files[0])} data-testid="verification-file-input" />
                  <button onClick={() => fileRef.current?.click()} disabled={busy}
                    className="px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 disabled:opacity-50 border border-red-300 text-red-700 bg-white"
                    data-testid="verification-upload-btn">
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload a photo
                  </button>
                </div>
              )}
              {error && <p className="text-xs text-red-700 mt-2" data-testid="verification-error">{error}</p>}
            </>
          )}
        </div>
        <ShieldCheck size={18} className="text-gray-300" />
      </div>
    </div>
  );
};

export default ParentVerificationCard;
